import { connect } from 'react-redux';
import { get } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';

import { showErrorMessage } from '@actions/messageconfirmation';

import utilsXLS from '@commons/utils/makeXLS';

import ExportModalContent from '@lib/inpulse/ExportModal';

import { getEntityUnit } from '@stocks/utils';
import { PAST_STOCKS_TYPE } from '@stocks/utils/constants';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';

const getMainsSheetColumnsSettings = (pastStockType) => {
  const columns = [
    {
      name: i18next.t(
        pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_RECIPES
          ? 'GENERAL.RECIPE'
          : 'GENERAL.NAME',
      ),
      propertyKey: 'name',
    },
    {
      name: i18next.t('GENERAL.UNIT'),
      propertyKey: 'unit',
      transform: (value) =>
        getEntityUnit(value) === 'u.' ? i18next.t('GENERAL.UNIT') : getEntityUnit(value),
    },
    {
      name: i18next.t('STOCKS.STOCKS.THEORETICAL_STOCK_SINGULAR_UNIT'),
      propertyKey: 'theoricalStockUnit',
    },
    {
      name: i18next.t(
        pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_RECIPES
          ? 'STOCKS.STOCKS.REAL_STOCK_SINGULAR_UNIT_WITH_RECIPES'
          : 'STOCKS.STOCKS.REAL_STOCK_SINGULAR_UNIT_WITHOUT_RECIPES',
      ),
      propertyKey: 'realStockUnit',
    },
    {
      name: i18next.t('STOCKS.STOCKS.GAP_STOCK_SINGULAR_UNIT'),
      propertyKey: 'stockGapUnit',
    },
    {
      type: 'currency',
      name: i18next.t('STOCKS.STOCKS.THEORETICAL_STOCK_SINGULAR_CURRENCY'),
      propertyKey: 'theoricalStockTurnover',
    },
    {
      name: i18next.t(
        pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_RECIPES
          ? 'STOCKS.STOCKS.REAL_STOCK_SINGULAR_CURRENCY_WITH_RECIPES'
          : 'STOCKS.STOCKS.REAL_STOCK_SINGULAR_CURRENCY_WITHOUT_RECIPES',
      ),
      propertyKey: 'realStockTurnover',
    },
    {
      name: i18next.t('STOCKS.STOCKS.GAP_STOCK_SINGULAR_CURRENCY'),
      propertyKey: 'stockGapTurnover',
    },
    {
      name: i18next.t('STOCKS.PAST_STOCKS.GAP_PERCENT'),
      propertyKey: 'stockGapPercentage',
    },
  ];

  if (pastStockType !== PAST_STOCKS_TYPE.CENTRAL_KITCHEN_RECIPES) {
    columns.splice(4, 0, {
      name: i18next.t('STOCKS.STOCKS.REAL_STOCK_SINGULAR_UNIT_WITH_RECIPES'),
      propertyKey: 'realStockWithRecipesUnit',
    });

    columns.splice(8, 0, {
      name: i18next.t('STOCKS.STOCKS.REAL_STOCK_SINGULAR_CURRENCY_WITH_RECIPES'),
      propertyKey: 'realStockWithRecipesTurnover',
    });
  }

  return columns;
};

const INFORMATION_SHEET_COLUMNS_SETTINGS = (clientStoreName) => [
  {
    name: clientStoreName,
    propertyKey: 'storeName',
  },
  {
    name: i18next.t('GENERAL.DATE'),
    propertyKey: 'date',
  },
  {
    name: i18next.t('STOCKS.PAST_STOCKS.EXPORT_COLUMN_NAME_TOTAL_THEORICAL_STOCK'),
    propertyKey: 'totalTheoreticalStock',
  },
  {
    name: i18next.t('STOCKS.PAST_STOCKS.EXPORT_COLUMN_NAME_TOTAL_ACTUAL_STOCK'),
    propertyKey: 'totalRealStock',
  },
];

export const createInformationSheet = (
  storesPastStock,
  selectedStoreName,
  date,
  clientStoreName,
  pastStockType,
) => {
  const data = [];

  const { totalRealStock, totalTheoreticalStock } = storesPastStock.reduce(
    (result, data) => {
      const realStockValue =
        pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_RECIPES
          ? get(data, 'realStock.turnover', 0)
          : get(data, 'realStockWithRecipes.turnover', 0);

      const theoreticalStockValue = get(data, 'theoricalStock.turnover', 0);

      result.totalRealStock += realStockValue;
      result.totalTheoreticalStock += theoreticalStockValue;

      return result;
    },
    {
      totalRealStock: 0,
      totalTheoreticalStock: 0,
    },
  );

  data.push({
    date: moment(date).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
    storeName: selectedStoreName,
    totalRealStock: totalRealStock == null ? '' : Math.round(totalRealStock * 100) / 100,
    totalTheoreticalStock:
      totalTheoreticalStock == null ? '' : Math.round(totalTheoreticalStock * 100) / 100,
  });

  return {
    data,
    title: i18next.t('STOCKS.PAST_STOCKS.EXPORT_SHEET_NAME_INFORMATION'),
    headers: INFORMATION_SHEET_COLUMNS_SETTINGS(clientStoreName),
  };
};

export const StocksAnalyticsPastExportModal = (props) => {
  const {
    currency,
    params: { title, mainSheetData, pastStockType, selectedStoreName, date, clientStoreName },
    closeModal,
    showErrorMessage,
  } = props;

  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [titleModal, setTitleModal] = useState(title || '');

  const mainSheetColumns = getMainsSheetColumnsSettings(pastStockType);

  useEffect(() => {
    (async function launchExport() {
      try {
        const mainSheet = utilsXLS.generateDefaultSheet(
          i18next.t('FEATURE.STOCKS.PAST'),
          mainSheetColumns,
          mainSheetData,
          currency,
        );

        const informationDataSheet = createInformationSheet(
          mainSheetData,
          selectedStoreName,
          date,
          clientStoreName,
          pastStockType,
        );

        const informationSheet = utilsXLS.generateDefaultSheet(
          informationDataSheet.title,
          informationDataSheet.headers,
          informationDataSheet.data,
          currency,
        );

        utilsXLS.makeXLS(title, [mainSheet, informationSheet]);

        setTitleModal(i18next.t('GENERAL.EXPORT_SUCCESS'));
        setIsLoading(false);
      } catch (error) {
        showErrorMessage(i18next.t('GENERAL.EXPORT_FAILURE'));
        exitModal();
      }
    })();
  }, []);

  useEffect(() => {
    if (isLoading && progress < 100) {
      setProgress(100);
    }
  }, [progress]);

  const exitModal = () => {
    setIsLoading(false);
    closeModal();
  };

  return (
    <ExportModalContent
      {...props}
      closeModal={closeModal}
      exitModal={exitModal}
      isLoading={isLoading}
      progress={progress}
      setLoading={setIsLoading}
      titleModal={titleModal}
    />
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(StocksAnalyticsPastExportModal);
